import React, { useState } from "react";
import { toast } from "sonner";
import { AppWindow, Link2, Save } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/common";

export default function SpecialAppEditor({ client, onSaved, readOnly = false }) {
  const SA_STATUS = { online: "Online", in_pruefung: "In Prüfung", offline: "Offline" };
  const empty = {
    special_app_name: client.special_app_name || "",
    special_app_url: client.special_app_url || "",
    special_app_preview_url: client.special_app_preview_url || "",
    special_app_active_url: client.special_app_active_url || "produktion",
    special_app_status: client.special_app_status || "",
    app_domain: client.app_domain || "",
    tool_model: client.tool_model || "",
    tool_price_monthly: client.tool_price_monthly ?? "",
    tool_setup_fee: client.tool_setup_fee ?? "",
    tech_notes: client.tech_notes || "",
  };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]";
  const lastChecked = (client.special_app_last_checked || "").slice(0, 16).replace("T", " ") || "—";

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, {
        special_app_name: f.special_app_name, special_app_url: f.special_app_url,
        special_app_preview_url: f.special_app_preview_url, special_app_active_url: f.special_app_active_url,
        special_app_status: f.special_app_status, app_domain: f.app_domain,
        tool_model: f.tool_model, tech_notes: f.tech_notes,
        tool_price_monthly: f.tool_price_monthly === "" ? null : Number(f.tool_price_monthly),
        tool_setup_fee: f.tool_setup_fee === "" ? null : Number(f.tool_setup_fee),
      });
      toast.success("Spezial-App gespeichert");
      onSaved();
    } catch { toast.error("Speichern fehlgeschlagen"); } finally { setSaving(false); }
  };

  return (
    <Card className="p-6" data-testid="special-app-editor">
      <fieldset disabled={readOnly} className="min-w-0">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <AppWindow className="w-5 h-5 text-[#A3E635]" />
          <h3 className="font-bold">Verbundene Spezial-App</h3>
        </div>
        {f.special_app_name && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border bg-white/10 text-gray-200 border-white/15" data-testid="sa-status-badge">
            {SA_STATUS[f.special_app_status] || "In Prüfung"}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-5">Externe, verbundene App (Fundio Pocket Admin). Nur Verlinkung – kein iFrame, keine zweite App, keine doppelte Benutzerverwaltung. Im Kunden-Cockpit ist ausschliesslich die Produktions-URL sichtbar.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Spezial-App Name</label>
          <input className={inp} data-testid="sa-name" placeholder="Fundio Pocket Admin" value={f.special_app_name} onChange={(e) => set("special_app_name", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Status</label>
          <select className={inp} data-testid="sa-status" value={f.special_app_status} onChange={(e) => set("special_app_status", e.target.value)}>
            <option value="">— wählen —</option>
            <option value="online">Online</option>
            <option value="in_pruefung">In Prüfung</option>
            <option value="offline">Offline</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Produktions-URL <span className="text-[#A3E635]">(Kunden-Cockpit)</span></label>
          <input className={inp} data-testid="sa-url" placeholder="https://app.fundio.ch/pocket-admin/login" value={f.special_app_url} onChange={(e) => set("special_app_url", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Preview-URL <span className="text-amber-300">(nur intern)</span></label>
          <input className={inp} data-testid="sa-preview-url" placeholder="https://smart-inventory-294.preview.emergentagent.com/" value={f.special_app_preview_url} onChange={(e) => set("special_app_preview_url", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Aktive Kunden-URL</label>
          <select className={inp} data-testid="sa-active-url" value={f.special_app_active_url} onChange={(e) => set("special_app_active_url", e.target.value)}>
            <option value="produktion">Produktions-URL</option>
            <option value="preview">Preview-URL (intern)</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">App-Domain</label>
          <input className={inp} data-testid="sa-domain" placeholder="app.fundio.ch" value={f.app_domain} onChange={(e) => set("app_domain", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Tool / Modell</label>
          <input className={inp} data-testid="sa-tool" placeholder="Fundio Pocket Admin" value={f.tool_model} onChange={(e) => set("tool_model", e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Preis / Mt. (CHF)</label>
            <input type="number" className={inp} data-testid="sa-price" placeholder="390" value={f.tool_price_monthly} onChange={(e) => set("tool_price_monthly", e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Setup (CHF)</label>
            <input type="number" className={inp} data-testid="sa-setup" placeholder="990" value={f.tool_setup_fee} onChange={(e) => set("tool_setup_fee", e.target.value)} />
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
        <div><div className="text-gray-500 text-xs">Integrationsart</div><div className="font-medium" data-testid="sa-integration-type">{client.special_app_integration_type || "Nur Verlinkung"}</div></div>
        <div><div className="text-gray-500 text-xs">Daten-Sync</div><div className="font-medium" data-testid="sa-data-sync">{client.special_app_data_sync || "Noch nicht aktiv"}</div></div>
        <div><div className="text-gray-500 text-xs">Letzte Prüfung</div><div className="font-medium" data-testid="sa-last-checked">{lastChecked}</div></div>
      </div>

      <div className="mt-4">
        <label className="text-xs text-gray-400 mb-1 block">Technische Notizen</label>
        <textarea className={`${inp} min-h-[72px]`} data-testid="sa-notes" placeholder="Hinweise zur Anbindung, Login, Domain ..." value={f.tech_notes} onChange={(e) => set("tech_notes", e.target.value)} />
      </div>

      <div className="flex items-center gap-3 mt-5">
        {!readOnly && <button onClick={save} disabled={saving} data-testid="sa-save"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4" /> Spezial-App speichern
        </button>}
        {f.special_app_url && (
          <a href={f.special_app_url} target="_blank" rel="noreferrer" data-testid="sa-open-prod"
            className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Produktion öffnen
          </a>
        )}
        {f.special_app_preview_url && (
          <a href={f.special_app_preview_url} target="_blank" rel="noreferrer" data-testid="sa-open-preview"
            className="bg-white/5 border border-amber-400/20 text-amber-200 hover:bg-white/10 font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Preview öffnen (intern)
          </a>
        )}
      </div>
      </fieldset>
    </Card>
  );
}
